import { Box, BoxProps, IconButton } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  gridClasses,
} from '@mui/x-data-grid';
import React, { FC } from 'react';
import ActionsIcon from '@mui/icons-material/MoreHorizOutlined';

export interface CustomDataGridProps extends DataGridProps {
  wrapperProps?: BoxProps;
  withActionsMenu?: boolean;
  onActionClick?: (id: string | number) => void;
}

/**
 * Creates an actions column with a 3-dots menu
 */
export const createActionsColumn = (
  onActionClick?: (id: string | number) => void,
): GridColDef => ({
  field: 'actions',
  headerName: '',
  width: 50,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => null,
  renderCell: (params) => (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        if (onActionClick) {
          onActionClick(params.row.id);
        }
      }}
    >
      <ActionsIcon fontSize="small" />
    </IconButton>
  ),
});

/**
 * A custom DataGrid component that provides consistent styling across the application
 */
export const CustomDataGrid: FC<CustomDataGridProps> = ({
  wrapperProps,
  sx,
  columns,
  withActionsMenu = false,
  onActionClick,
  ...rest
}) => {
  // Prepare columns with actions if needed
  const finalColumns = React.useMemo(() => {
    if (withActionsMenu) {
      return [createActionsColumn(onActionClick), ...columns];
    }
    return columns;
  }, [columns, withActionsMenu, onActionClick]);

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        ...wrapperProps?.sx,
      }}
      {...wrapperProps}
    >
      <DataGrid
        hideFooterPagination
        disableColumnMenu
        showCellVerticalBorder
        showColumnVerticalBorder
        disableRowSelectionOnClick
        columns={finalColumns}
        sx={{
          // Remove focus outlines
          [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none !important',
            },

          [`& .${gridClasses.columnHeaders}`]: {
            backgroundColor: '#f5f5f5',
            minHeight: '36px !important',
            maxHeight: '36px !important',
            lineHeight: '36px',
            [`& .${gridClasses.columnHeader}`]: {
              backgroundColor: ({ palette }) => palette.background.default,
              minHeight: '36px !important',
              maxHeight: '36px !important',
              lineHeight: '36px',
              borderRight: 'none',
              [`& .${gridClasses.columnHeaderTitle}`]: {
                fontSize: '0.875rem',
                color: 'text.secondary',
              },
            },
            [`& .${gridClasses.columnSeparator}`]: {
              display: 'none',
            },
          },
          [`& .${gridClasses.cell}`]: {
            borderLeft: 'none',
            borderRight: 'none',
          },
          ...sx,
        }}
        {...rest}
      />
    </Box>
  );
};

/**
 * A wrapper component for cells to maintain consistent styling
 */
export const CellWrapper: FC<BoxProps> = (props) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="flex-start"
    width="100%"
    height="100%"
    {...props}
  />
);

export default CustomDataGrid;
